import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { 
  ReleaseResult, 
  ProjectReleaseStatus, 
  ReleaseMonitorStatus,
  GitLabPipeline,
  GitLabMergeRequest 
} from '../types/gitlab';
import { gitlabService } from '../services/gitlabService';

type TagSeverity = 'success' | 'danger' | 'info' | 'warning';

export interface ReleaseMonitorProps {
  results: ReleaseResult[];
  releaseName: string;
  onClose: () => void;
  embedded?: boolean;
}

const getPipelineMonitorStatus = (pipelineStatus: string): ReleaseMonitorStatus => {
  switch (pipelineStatus) {
    case 'success':
      return 'success';
    case 'failed':
    case 'canceled':
      return 'failed';
    case 'running':
    case 'pending':
    case 'created':
    case 'waiting_for_resource':
    case 'preparing':
      return 'in_progress';
    default:
      return 'pending';
  }
};

const getStatusIcon = (status: ReleaseMonitorStatus) => {
  const iconMap: Record<ReleaseMonitorStatus, string> = {
    success: 'pi pi-check-circle',
    failed: 'pi pi-times-circle',
    in_progress: 'pi pi-spin pi-spinner',
    pending: 'pi pi-clock',
  };
  return iconMap[status];
};

const getStatusColor = (status: ReleaseMonitorStatus) => {
  const colorMap: Record<ReleaseMonitorStatus, string> = {
    success: 'text-green-500',
    failed: 'text-red-500',
    in_progress: 'text-blue-500',
    pending: 'text-yellow-500',
  };
  return colorMap[status];
};

const getStatusSeverity = (status: ReleaseMonitorStatus): TagSeverity => {
  const severityMap: Record<ReleaseMonitorStatus, TagSeverity> = {
    success: 'success',
    failed: 'danger',
    in_progress: 'info',
    pending: 'warning',
  };
  return severityMap[status];
};

const getBorderClass = (status: ReleaseMonitorStatus) => {
  const borderMap: Record<ReleaseMonitorStatus, string> = {
    success: 'border-left-3 border-green-500',
    failed: 'border-left-3 border-red-500',
    in_progress: 'border-left-3 border-blue-500',
    pending: 'border-left-3 border-yellow-500',
  };
  return borderMap[status];
};

const pollTagStatus = async (
  status: ProjectReleaseStatus,
  releaseName: string
): Promise<ProjectReleaseStatus> => {
  const pipeline = await gitlabService.getTagPipeline(
    status.project.id,
    status.tagName || releaseName
  );

  if (pipeline) {
    return {
      ...status,
      pipeline,
      status: getPipelineMonitorStatus(pipeline.status),
    };
  }
  return { ...status, status: 'pending' as ReleaseMonitorStatus };
};

const pollMRStatus = async (
  status: ProjectReleaseStatus
): Promise<ProjectReleaseStatus> => {
  if (!status.mergeRequestIid) {
    return { ...status, status: 'failed' as ReleaseMonitorStatus, error: 'No MR IID' };
  }

  const mr = await gitlabService.getMergeRequest(status.project.id, status.mergeRequestIid);

  if (!mr) {
    return { ...status, status: 'failed' as ReleaseMonitorStatus, error: 'MR not found' };
  }

  if (mr.state === 'merged') {
    const pipeline = mr.head_pipeline;
    const isSuccess = !pipeline || pipeline.status === 'success';
    const isFailed = pipeline?.status === 'failed' || pipeline?.status === 'canceled';
    
    let mrStatus: ReleaseMonitorStatus = 'in_progress';
    if (isFailed) mrStatus = 'failed';
    else if (isSuccess) mrStatus = 'success';

    return {
      ...status,
      mergeRequest: mr,
      mrPipeline: pipeline || undefined,
      status: mrStatus,
    };
  }
  
  if (mr.state === 'closed') {
    return { ...status, mergeRequest: mr, status: 'failed' as ReleaseMonitorStatus, error: 'MR was closed' };
  }

  const pipeline = mr.head_pipeline;
  const pipelineStatus = pipeline ? getPipelineMonitorStatus(pipeline.status) : 'pending';
  
  return {
    ...status,
    mergeRequest: mr,
    mrPipeline: pipeline || undefined,
    status: pipelineStatus === 'success' ? 'in_progress' : pipelineStatus,
  };
};

const ReleaseMonitor: React.FC<ReleaseMonitorProps> = ({ results, releaseName, onClose, embedded = false }) => {
  const [statuses, setStatuses] = useState<ProjectReleaseStatus[]>([]);
  const [isPolling, setIsPolling] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    const initialStatuses: ProjectReleaseStatus[] = results.map((result) => {
      if (!result.success) {
        return {
          project: result.project,
          releaseType: 'tag' as const,
          status: 'failed' as ReleaseMonitorStatus,
          error: result.error,
        };
      }

      return {
        project: result.project,
        releaseType: result.releaseType || 'tag',
        status: 'pending' as ReleaseMonitorStatus,
        tagName: result.tag?.name,
        mergeRequestIid: result.mergeRequestIid,
      };
    });

    setStatuses(initialStatuses);
  }, [results]);

  const pollStatuses = useCallback(async () => {
    const updatedStatuses = await Promise.all(
      statuses.map(async (status) => {
        if (status.status === 'success' || status.status === 'failed') {
          return status;
        }

        try {
          if (status.releaseType === 'tag') {
            return await pollTagStatus(status, releaseName);
          } else {
            return await pollMRStatus(status);
          }
        } catch (error) {
          console.error('Error polling status:', error);
          return status;
        }
      })
    );

    setStatuses(updatedStatuses);
    setLastUpdate(new Date());

    const allComplete = updatedStatuses.every(
      (s) => s.status === 'success' || s.status === 'failed'
    );
    if (allComplete) {
      setIsPolling(false);
    }
  }, [statuses, releaseName]);

  useEffect(() => {
    if (!isPolling || statuses.length === 0) return;

    const interval = setInterval(pollStatuses, 5000);
    const timeout = setTimeout(pollStatuses, 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPolling, pollStatuses, statuses.length]);

  const getPipelineStatusTag = (pipeline: GitLabPipeline | undefined) => {
    if (!pipeline) {
      return <Tag value="No Pipeline" severity="warning" />;
    }

    const severityMap: Record<string, TagSeverity> = {
      success: 'success',
      failed: 'danger',
      canceled: 'danger',
      running: 'info',
      pending: 'warning',
      created: 'warning',
    };

    return (
      <a href={pipeline.web_url} target="_blank" rel="noopener noreferrer" className="no-underline">
        <Tag 
          value={pipeline.status.toUpperCase()} 
          severity={severityMap[pipeline.status] || 'warning'}
          icon={pipeline.status === 'running' ? 'pi pi-spin pi-spinner' : undefined}
        />
      </a>
    );
  };

  const getMRStatusTag = (mr: GitLabMergeRequest | undefined) => {
    if (!mr) {
      return <Tag value="Loading..." severity="warning" />;
    }

    const severityMap: Record<string, TagSeverity> = {
      merged: 'success',
      opened: 'info',
      closed: 'danger',
      locked: 'warning',
    };

    return (
      <a href={mr.web_url} target="_blank" rel="noopener noreferrer" className="no-underline">
        <Tag value={mr.state.toUpperCase()} severity={severityMap[mr.state] || 'warning'} />
      </a>
    );
  };

  const successCount = statuses.filter((s) => s.status === 'success').length;
  const failedCount = statuses.filter((s) => s.status === 'failed').length;
  const inProgressCount = statuses.filter((s) => s.status === 'in_progress' || s.status === 'pending').length;
  const allReady = successCount === statuses.length && statuses.length > 0;

  return (
    <div className="release-monitor">
      <Card className="mb-4">
        <div className="flex align-items-center justify-content-between mb-4">
          <div className="flex align-items-center gap-3">
            <i className="pi pi-chart-line text-2xl text-primary"></i>
            <div>
              <h2 className="text-xl font-semibold m-0">Release Monitor</h2>
              <p className="text-color-secondary m-0 text-sm">Tracking: {releaseName}</p>
            </div>
          </div>
          <div className="flex align-items-center gap-3">
            {isPolling && (
              <div className="flex align-items-center gap-2 text-color-secondary">
                <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                <span className="text-sm">Auto-refreshing...</span>
              </div>
            )}
            {!embedded && (
              <Button label="Close" icon="pi pi-times" className="p-button-outlined" onClick={onClose} />
            )}
          </div>
        </div>

        <div className="flex gap-3 mb-4 justify-content-center">
          {allReady ? (
            <Tag value="All Projects Ready!" severity="success" className="text-lg py-2 px-4" />
          ) : (
            <>
              <Tag value={successCount + ' Ready'} severity="success" icon="pi pi-check" />
              {inProgressCount > 0 && (
                <Tag value={inProgressCount + ' In Progress'} severity="info" icon="pi pi-spinner" />
              )}
              {failedCount > 0 && (
                <Tag value={failedCount + ' Failed'} severity="danger" icon="pi pi-times" />
              )}
            </>
          )}
        </div>

        <div className="text-right text-color-secondary text-sm mb-3">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>

        <div className="release-status-list">
          {statuses.map((status) => (
            <div
              key={status.project.id}
              className={'release-status-item p-3 mb-2 border-round surface-ground ' + getBorderClass(status.status)}
            >
              <div className="flex align-items-center justify-content-between">
                <div className="flex align-items-center gap-3 flex-1">
                  <i className={getStatusIcon(status.status) + ' ' + getStatusColor(status.status) + ' text-xl'}></i>
                  <div>
                    <div className="font-semibold">{status.project.name}</div>
                    <div className="text-color-secondary text-sm">
                      {status.releaseType === 'tag' ? (
                        <span><i className="pi pi-tag mr-1"></i>Tag: {status.tagName || releaseName}</span>
                      ) : (
                        <span><i className="pi pi-code mr-1"></i>Merge Request (UAT to {status.project.default_branch})</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex align-items-center gap-3">
                  {status.releaseType === 'tag' ? (
                    <div className="text-sm">
                      <span className="text-color-secondary mr-2">Pipeline:</span>
                      {getPipelineStatusTag(status.pipeline)}
                    </div>
                  ) : (
                    <>
                      <div className="text-sm">
                        <span className="text-color-secondary mr-2">MR:</span>
                        {getMRStatusTag(status.mergeRequest)}
                      </div>
                      <div className="text-sm">
                        <span className="text-color-secondary mr-2">Pipeline:</span>
                        {getPipelineStatusTag(status.mrPipeline)}
                      </div>
                    </>
                  )}
                  <Tag 
                    value={status.status === 'success' ? 'READY' : status.status.toUpperCase().replace('_', ' ')} 
                    severity={getStatusSeverity(status.status)}
                    className="ml-2"
                  />
                </div>
              </div>

              {status.error && (
                <div className="mt-2 text-red-500 text-sm">
                  <i className="pi pi-exclamation-triangle mr-1"></i>
                  {status.error}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ReleaseMonitor;